import imageUrlBuilder from "@sanity/image-url";
import { SanityImageSource } from "@sanity/image-url/lib/types/types";

import { DispenserDialogContent } from "@/components/DispenserDialogContent";
import { Text } from "@/components/elements/Text";
import useBreakpointTest from "@/hooks/useBreakpointTest";
import useDialog from "@/hooks/useDialog";
import { client } from "@/sanity/lib/client";

import { Carousel } from "../Carousel";
import { Button } from "../elements/Button";
import { Slide } from "./Slide";
import styles from "./styles.module.css";

type CarouselItem = {
  header: string;
  copy: string;
  image: SanityImageSource;
  icon: string;
  popup?: boolean;
  popup_title?: string;
  popup_copy?: string;
  popup_video?: string;
  popup_button_text?: string;
};

interface HowItWorksProps {
  title: string;
  carousel: CarouselItem[];
  locale: string;
}

export const HowItWorks = ({ title, carousel }: HowItWorksProps) => {
  // Sanity image builder
  const builder = imageUrlBuilder(client());
  function urlFor(source) {
    return builder.image(source);
  }

  const { showDialog } = useDialog();

  const showPopup = ({ title, copy, videoSrc }) => {
    const content = <DispenserDialogContent copy={copy} videoSrc={videoSrc} />;
    showDialog(content, title);
  };

  // Adapt sanity data to Carousel component
  let steps;
  if (carousel && carousel.length > 0) {
    steps = carousel.map((item: CarouselItem) => {
      const step: {
        header: string;
        copy: string;
        image: string;
        icon: string;
        cta?: JSX.Element;
      } = {
        header: item.header,
        copy: item.copy,
        image: urlFor(item.image).width(800).url(),
        icon: item.icon,
      };

      if (item.popup) {
        step.cta = (
          <Button
            onClick={() =>
              showPopup({
                title: item.popup_title,
                copy: item.popup_copy,
                videoSrc: item.popup_video, //needs to be a different format to work here
              })
            }
            className={styles.button}
          >
            {item.popup_button_text}
          </Button>
        );
      }
      return step;
    });
  }

  const isTablet = useBreakpointTest("sm");

  const carouselConfig = {
    slides: {
      perView: isTablet ? 4 : 1,
      spacing: 32,
    },
    mode: "snap",
  };

  return (
    <div className={styles.container}>
      <Text as="h2" size="l" className={styles.title}>
        {title}
      </Text>
      <Carousel slides={steps.map(Slide)} carouselConfig={carouselConfig} />
    </div>
  );
};
